import type {
  EmailTemplate,
  TemplateDynamicField,
  ResolvedTemplate,
  TemplateDataContext,
  TemplateModuleSource,
} from '@/types/emailTemplate'
import { DYNAMIC_FIELD_CLASS, DYNAMIC_TABLE_CLASS } from '@/types/emailTemplate'
import type { TemplateDynamicTableColumn } from '@/types/emailTemplate'

import { formatLocalDate as coreFormatLocalDate, formatNumber as coreFormatNumber, getFormatSettings } from '@/composables/useFormat'
import * as coreDate from '@/utils/format/formatCore'
import { DATE_FORMATS, type DateFormatType } from '@/types/format-settings'
import { useCatalogsStore } from '@/stores/catalogs'
import { findStatusLabel, allStatusSetStatuses, getStatusSetEntries } from '@/utils/shipmentStatusSets'
import { usePurchaseOrderStatus } from '@/composables/usePurchaseOrderStatus'
/**
 * Composable para resolver plantillas de correo dinámicas.
 * Reemplaza los campos dinámicos (chips HTML) por valores reales
 * extraídos del contexto de datos (embarque, OC, etc.).
 */
export function useTemplateResolver() {
  const catalogsStore = useCatalogsStore()
  const { getStatusLabel: getPurchaseOrderStatusLabel } = usePurchaseOrderStatus()

  /**
   * Los estados se guardan como ID (ej: 'BOOKING_REQUEST') y su etiqueta legible
   * vive en la configuración de la compañía. Devuelve la etiqueta configurada,
   * o undefined si el campo no es un estado.
   */
  function resolveStatusLabel(
    module: string,
    fieldId: string,
    value: any,
    context: TemplateDataContext
  ): string | undefined {
    if (fieldId !== 'status' || typeof value !== 'string' || !value) return undefined

    if (module === 'shipments') {
      const config = catalogsStore.configs?.SHIPMENT_STATUS as any
      const label = findStatusLabel(config, context.shipments, value)
      if (label) return label
      // Último intento sin filtrar visibles: un estado oculto sigue teniendo su etiqueta
      const buckets: any[] = Array.isArray(config?.status) ? [...config.status] : []
      for (const list of Object.values(config?.statusByMode || {})) {
        if (Array.isArray(list)) buckets.push(...list)
      }
      buckets.push(...allStatusSetStatuses(config))
      // También las listas inactivas (de otro campo base), igual que la CF
      for (const entry of getStatusSetEntries(config)) buckets.push(...entry.statuses)
      return buckets.find(st => st?.id === value && st.label)?.label || value
    }

    if (module === 'purchaseOrders') {
      return getPurchaseOrderStatusLabel(value) || value
    }

    return undefined
  }

  /**
   * Etiqueta configurada para un estado de contenedor (SHIPMENT_STATUS.statusByContainer)
   */
  function resolveContainerStatusLabel(statusId: string): string {
    const statuses = (catalogsStore.configs?.SHIPMENT_STATUS as any)?.statusByContainer
    if (!Array.isArray(statuses)) return statusId
    return statuses.find((s: any) => s?.id === statusId)?.label || statusId
  }

  /**
   * Obtiene un valor anidado de un objeto usando dot notation.
   * Ejemplo: getNestedValue({ milestones: { eta: '2025-01-01' } }, 'milestones.eta') → '2025-01-01'
   */
  function getNestedValue(obj: Record<string, any>, path: string): any {
    if (!obj || !path) return undefined
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined
    }, obj)
  }

  /**
   * Busca un valor en un objeto: primero directo (dot notation), luego
   * busca recursivamente en sub-objetos anidados (milestones, carriers, etc.)
   * Esto permite que fieldId="etd" encuentre milestones.etd automáticamente.
   */
  function deepFindValue(obj: Record<string, any>, fieldId: string): any {
    // 1. Intento directo (dot notation)
    const direct = getNestedValue(obj, fieldId)
    if (direct !== undefined && direct !== null) return direct

    // 2. Buscar en sub-objetos de primer nivel (milestones, carriers, etc.)
    for (const key of Object.keys(obj)) {
      const child = obj[key]
      if (child && typeof child === 'object' && !Array.isArray(child) && !(child.seconds)) {
        const nested = getNestedValue(child, fieldId)
        if (nested !== undefined && nested !== null) return nested
      }
    }

    // 3. Retornar el valor directo original (puede ser null si no se encontró mejor)
    return direct
  }

  /**
   * Genera una tabla HTML automática a partir de un array de objetos.
   * Se usa cuando un campo como quotationContainers/quotationCharges se inserta como campo individual.
   */
  function buildAutoTableHtml(items: Record<string, any>[]): string {
    if (!items.length) return ''

    const labelMap: Record<string, string> = {
      type: 'Tipo', packages: 'Bultos', grossWeight: 'Peso Bruto', volume: 'Volumen',
      concept: 'Concepto', category: 'Categoría', currency: 'Moneda',
      unitValue: 'Valor Unit.', tax: 'IVA', total: 'Total', description: 'Descripción',
    }

    const keys = Object.keys(items[0]).filter(k => !k.startsWith('_'))
    const headerCells = keys
      .map(k => `<th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e5e7eb;background-color:#f9fafb;font-size:13px;font-weight:600;color:#374151;">${labelMap[k] || k}</th>`)
      .join('')

    const rows = items.map(item => {
      const cells = keys.map(k => {
        let val = item[k]
        if (val === null || val === undefined) val = '-'
        else if (typeof val === 'number') val = coreFormatNumber(val, { intent: 'money' })
        else val = String(val)
        return `<td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:13px;color:#4b5563;">${val}</td>`
      }).join('')
      return `<tr>${cells}</tr>`
    }).join('')

    return `<table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:8px;margin:12px 0;"><thead><tr>${headerCells}</tr></thead><tbody>${rows}</tbody></table>`
  }

  /**
   * Detecta si un objeto tiene estructura de party (IShipmentContactData)
   */
  function isPartyObject(value: any): boolean {
    return (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      typeof value.name === 'string' &&
      value.address &&
      typeof value.address === 'object'
    )
  }

  /**
   * Formatea un objeto party como bloque HTML estructurado.
   * Renderiza: nombre, dirección completa, teléfono(s) y email(s) — uno por línea.
   */
  function formatPartyHtml(party: Record<string, any>): string {
    const lines: string[] = []

    if (party.name && party.name !== '-') {
      lines.push(`<strong>${party.name}</strong>`)
    }

    if (party.address && typeof party.address === 'object') {
      const addr = party.address
      if (addr.addressLine1 && addr.addressLine1 !== '-') lines.push(addr.addressLine1)
      if (addr.addressLine2 && addr.addressLine2 !== '-') lines.push(addr.addressLine2)

      const cityParts: string[] = []
      if (addr.cityName && addr.cityName !== '-') cityParts.push(addr.cityName)
      else if (addr.city && addr.city !== '-') cityParts.push(addr.city)
      if (addr.stateProvince && addr.stateProvince !== '-') cityParts.push(addr.stateProvince)
      if (addr.postalCode && addr.postalCode !== '-') cityParts.push(addr.postalCode)
      if (addr.countryName && addr.countryName !== '-') cityParts.push(addr.countryName)
      else if (addr.country && addr.country !== '-') cityParts.push(addr.country)
      if (cityParts.length) lines.push(cityParts.join(', '))
    }

    if (Array.isArray(party.contacts)) {
      for (const contact of party.contacts) {
        if (!contact || typeof contact !== 'object') continue
        if (contact.contactName) lines.push(contact.contactName)
        if (contact.contactPhone) {
          const phone = contact.countryCode
            ? `${contact.countryCode} ${contact.contactPhone}`
            : contact.contactPhone
          lines.push(`Tel. ${phone}`)
        }
        if (contact.contactEmail) lines.push(`E-mail: ${contact.contactEmail}`)
      }
    }

    if (lines.length === 0) return ''
    return lines.join('<br>')
  }

  /**
   * Formatea un valor según su tipo de campo
   */
  function formatFieldValue(value: any, field: TemplateDynamicField): string {
    if (value === null || value === undefined) {
      return field.fallbackValue || `[${field.fieldLabel}]`
    }

    // Timestamps de Firestore
    if (value && typeof value === 'object' && value.seconds) {
      return formatDate(value, field.dateFormat)
    }

    // Fechas ISO string
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const formatted = formatDate(value, field.dateFormat)
      if (formatted) return formatted
    }

    // Arrays de objetos (tablas embebidas como quotationContainers/quotationCharges) → auto-tabla
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        return buildAutoTableHtml(value)
      }
      return value.join(', ')
    }

    // Party objects (shipper, consignee, notify, etc.)
    if (isPartyObject(value)) {
      return formatPartyHtml(value)
    }

    // Objetos genéricos → omitir
    if (typeof value === 'object' && value !== null) {
      return ''
    }

    return String(value)
  }

  /**
   * Día que representa un campo de fecha, como Date en zona local.
   *
   * Misma regla que DynamicField en el detalle del módulo: los strings ISO se leen
   * textualmente (manda el día escrito, no la zona) y los instantes (Timestamp,
   * Date, número) se leen en la zona del usuario. El correo formateaba en UTC, así
   * que un Timestamp de medianoche UTC salía con un día más que el del embarque.
   */
  function toFieldDate(value: any): Date | null {
    if (typeof value === 'string') {
      const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (match) {
        const [, year, month, day] = match
        return new Date(Number(year), Number(month) - 1, Number(day))
      }
    }

    if (value && typeof value === 'object' && typeof value.seconds === 'number') {
      const fromSeconds = new Date(value.seconds * 1000)
      return isNaN(fromSeconds.getTime()) ? null : fromSeconds
    }

    const date = coreDate.toDateObject(value)
    return date && !isNaN(date.getTime()) ? date : null
  }

  function formatDate(value: any, format?: string): string {
    const date = toFieldDate(value)
    if (!date) return ''

    // Sin formato explícito manda la configuración de la compañía
    if (!format) return coreFormatLocalDate(date, { fallback: '' })

    // 'long' (mes en texto) no tiene equivalente configurable: es un estilo que
    // el autor del template eligió a propósito.
    if (format === 'long') {
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })
    }

    // El template pidió un formato concreto: se respeta sobre la configuración
    if (DATE_FORMATS.includes(format as DateFormatType)) {
      return coreDate.formatLocalDate(date, { ...getFormatSettings(), dateFormat: format as DateFormatType }, { fallback: '' })
    }
    return coreFormatLocalDate(date, { fallback: '' })
  }

  /**
   * Formatea un valor simple para celdas de tabla (sin fallback label)
   */
  function formatCellValue(value: any, fieldType?: string, dateFormat?: string): string {
    if (value === null || value === undefined) return '-'

    if (value && typeof value === 'object' && value.seconds) {
      return formatDate(value, dateFormat)
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      const formatted = formatDate(value, dateFormat)
      if (formatted) return formatted
    }

    if (Array.isArray(value)) return value.join(', ')

    if (fieldType === 'currency' || fieldType === 'number') {
      const num = Number(value)
      if (!isNaN(num)) {
        return coreFormatNumber(num, { intent: fieldType === 'currency' ? 'money' : 'quantity' })
      }
    }

    return String(value)
  }

  /**
   * Resuelve bloques de tabla dinámica en el HTML.
   * Busca <div class="dynamic-table" ...> y genera tablas HTML con una fila por registro.
   * Soporta data-source opcional para tablas de cotización (quotationContainers, quotationCharges).
   */
  function resolveDynamicTables(
    html: string,
    context: TemplateDataContext
  ): string {
    const tableRegex = new RegExp(
      `<div[^>]*class="[^"]*${DYNAMIC_TABLE_CLASS}[^"]*"[^>]*data-module="([^"]*)"[^>]*data-columns=["']([^"']*?)["']\\s*(?:data-source="([^"]*)")?[^>]*>[\\s\\S]*?<\\/div>`,
      'gi'
    )

    return html.replace(tableRegex, (_match, module: string, columnsJsonRaw: string, source?: string) => {
      const columnsJson = columnsJsonRaw.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&')
      let columns: TemplateDynamicTableColumn[] = []
      try {
        columns = JSON.parse(columnsJson)
      } catch {
        return '<p style="color:#dc2626;">[Error: columnas de tabla inválidas]</p>'
      }

      // Si hay data-source, buscar en context[`${module}_${source}`]
      if (source) {
        const sourceKey = `${module}_${source}`
        const sourceItems = context[sourceKey] as Record<string, any>[] | undefined
        if (sourceItems && sourceItems.length > 0) {
          return buildTableHtml(columns, sourceItems, source)
        }
        return '<p style="color:#6b7280;font-style:italic;">Sin datos disponibles para la tabla</p>'
      }

      // Fallback: buscar en ${module}_items
      const itemsKey = `${module}_items` as keyof TemplateDataContext
      const items = context[itemsKey] as Record<string, any>[] | undefined

      if (!items || items.length === 0) {
        const singleData = context[module as TemplateModuleSource]
        if (singleData) {
          return buildTableHtml(columns, [singleData])
        }
        return '<p style="color:#6b7280;font-style:italic;">Sin datos disponibles para la tabla</p>'
      }

      return buildTableHtml(columns, items)
    })
  }

  /**
   * Construye el HTML de una tabla estilizada para correos electrónicos.
   */
  function buildTableHtml(columns: TemplateDynamicTableColumn[], items: Record<string, any>[], source?: string): string {
    const headerCells = columns
      .map(col => `<th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e5e7eb;background-color:#f9fafb;font-size:13px;font-weight:600;color:#374151;">${col.fieldLabel}</th>`)
      .join('')

    const rows = items.map(item => {
      const cells = columns.map(col => {
        const value = deepFindValue(item, col.fieldId)
        const isContainerStatus = source === 'containers' && col.fieldId === 'containerStatus'
        const formatted = isContainerStatus && value
          ? resolveContainerStatusLabel(String(value))
          : formatCellValue(value, col.fieldType, col.dateFormat)
        return `<td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:13px;color:#4b5563;">${formatted}</td>`
      }).join('')
      return `<tr>${cells}</tr>`
    }).join('')

    return `<table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:8px;margin:12px 0;">
      <thead><tr>${headerCells}</tr></thead>
      <tbody>${rows}</tbody>
    </table>`
  }

  /**
   * Resuelve todos los campos dinámicos en un string HTML.
   * Busca los <span class="dynamic-field" ...> y los reemplaza con el valor real.
   */
  function resolveHtml(
    html: string,
    context: TemplateDataContext,
    dynamicFields: TemplateDynamicField[]
  ): { resolved: string; unresolvedFields: TemplateDynamicField[] } {
    const unresolvedFields: TemplateDynamicField[] = []
    let resolved = html

    // Buscar todos los spans con clase dynamic-field
    const spanRegex = new RegExp(
      `<span[^>]*class="[^"]*${DYNAMIC_FIELD_CLASS}[^"]*"[^>]*data-module="([^"]*)"[^>]*data-field-id="([^"]*)"[^>]*>([^<]*)</span>`,
      'gi'
    )

    resolved = resolved.replace(spanRegex, (_match, module: string, fieldId: string, _innerText: string) => {
      const moduleData = context[module as TemplateModuleSource]
      const fieldDef = dynamicFields.find(
        (f) => f.module === module && f.fieldId === fieldId
      )

      if (!moduleData) {
        if (fieldDef) unresolvedFields.push(fieldDef)
        return fieldDef?.fallbackValue || `[${fieldDef?.fieldLabel || fieldId}]`
      }

      const value = deepFindValue(moduleData, fieldId)

      if (value === undefined || value === null) {
        if (fieldDef) unresolvedFields.push(fieldDef)
        return fieldDef?.fallbackValue || `[${fieldDef?.fieldLabel || fieldId}]`
      }

      const statusLabel = resolveStatusLabel(module, fieldId, value, context)
      if (statusLabel !== undefined) return statusLabel

      return formatFieldValue(value, fieldDef || {
        module: module as TemplateModuleSource,
        fieldId,
        fieldLabel: fieldId,
      })
    })

    // También reemplazar formato {{module.fieldId}} como fallback (para el subject)
    resolved = resolved.replace(/\{\{(\w+)\.([^}]+)\}\}/g, (_match, module: string, fieldId: string) => {
      const moduleData = context[module as TemplateModuleSource]
      const fieldDef = dynamicFields.find(
        (f) => f.module === module && f.fieldId === fieldId
      )

      if (!moduleData) {
        if (fieldDef) unresolvedFields.push(fieldDef)
        return fieldDef?.fallbackValue || `[${fieldDef?.fieldLabel || fieldId}]`
      }

      const value = deepFindValue(moduleData, fieldId)

      if (value === undefined || value === null) {
        if (fieldDef) unresolvedFields.push(fieldDef)
        return fieldDef?.fallbackValue || `[${fieldDef?.fieldLabel || fieldId}]`
      }

      const statusLabel = resolveStatusLabel(module, fieldId, value, context)
      if (statusLabel !== undefined) return statusLabel

      return formatFieldValue(value, fieldDef || {
        module: module as TemplateModuleSource,
        fieldId,
        fieldLabel: fieldId,
      })
    })

    return { resolved, unresolvedFields }
  }

  /**
   * Resuelve una plantilla completa (subject + bodyHtml) con datos reales
   */
  function resolveTemplate(
    template: EmailTemplate,
    context: TemplateDataContext
  ): ResolvedTemplate {
    // Primero resolver tablas dinámicas, luego campos individuales
    const bodyWithTables = resolveDynamicTables(template.bodyHtml, context)
    const subjectResult = resolveHtml(template.subject, context, template.dynamicFields)
    const bodyResult = resolveHtml(bodyWithTables, context, template.dynamicFields)

    const allUnresolved = [
      ...subjectResult.unresolvedFields,
      ...bodyResult.unresolvedFields,
    ]

    // Deduplicar
    const unique = allUnresolved.filter(
      (f, i, arr) =>
        arr.findIndex((x) => x.module === f.module && x.fieldId === f.fieldId) === i
    )

    return {
      subject: subjectResult.resolved,
      bodyHtml: bodyResult.resolved,
      unresolvedFields: unique,
    }
  }

  /**
   * Genera el HTML de un chip de campo dinámico para insertar en el editor
   */
  function createFieldChipHtml(field: TemplateDynamicField): string {
    const moduleLabel = field.module === 'shipments' ? 'Embarque' : 'OC'
    return `<span class="${DYNAMIC_FIELD_CLASS}" data-module="${field.module}" data-field-id="${field.fieldId}" data-field-label="${field.fieldLabel}" contenteditable="false" style="display:inline-block;padding:2px 8px;margin:0 2px;background-color:#dbeafe;color:#1d4ed8;border-radius:12px;font-size:13px;font-weight:500;border:1px solid #93c5fd;cursor:default;user-select:none;">${moduleLabel}: ${field.fieldLabel}</span>`
  }

  /**
   * Genera el HTML de un bloque de tabla dinámica para insertar en el editor.
   * Se muestra como un placeholder visual; al resolver la plantilla se reemplaza por la tabla real.
   * @param source - Fuente de datos opcional (ej: 'quotationContainers', 'quotationCharges')
   */
  function createTableChipHtml(module: TemplateModuleSource, columns: TemplateDynamicTableColumn[], source?: string): string {
    const sourceLabels: Record<string, string> = {
      quotationContainers: 'Contenedores de Cotización',
      quotationCharges: 'Cargos de Cotización',
      containers: 'Contenedores',
      items: 'Productos / Items',
      charges: 'Cargos',
    }
    const moduleLabel = source
      ? sourceLabels[source] || source
      : (module === 'purchaseOrders' ? 'Órdenes de Compra' : 'Embarques')
    const colLabels = columns.map(c => c.fieldLabel).join(', ')
    const columnsJson = JSON.stringify(columns).replace(/'/g, '&#39;')
    const sourceAttr = source ? ` data-source="${source}"` : ''
    return `<div class="${DYNAMIC_TABLE_CLASS}" data-module="${module}" data-columns='${columnsJson}'${sourceAttr} contenteditable="false" style="display:block;padding:12px 16px;margin:8px 0;background-color:#f0fdf4;color:#15803d;border-radius:8px;font-size:13px;font-weight:500;border:2px dashed #86efac;cursor:default;user-select:none;text-align:center;"><span style="font-weight:700;display:block;margin-bottom:4px;">Tabla: ${moduleLabel}</span><span style="font-size:11px;color:#4ade80;display:block;">Columnas: ${colLabels}</span><span style="font-size:11px;color:#86efac;display:block;margin-top:2px;">Se genera una fila por cada registro al enviar</span></div>`
  }

  /**
   * Extrae todos los campos dinámicos presentes en un HTML
   */
  function extractDynamicFields(html: string): TemplateDynamicField[] {
    const fields: TemplateDynamicField[] = []
    const spanRegex = new RegExp(
      `<span[^>]*class="[^"]*${DYNAMIC_FIELD_CLASS}[^"]*"[^>]*data-module="([^"]*)"[^>]*data-field-id="([^"]*)"[^>]*data-field-label="([^"]*)"[^>]*>[^<]*</span>`,
      'gi'
    )

    let match
    while ((match = spanRegex.exec(html)) !== null) {
      const [, module, fieldId, fieldLabel] = match
      if (!fields.find((f) => f.module === module && f.fieldId === fieldId)) {
        fields.push({
          module: module as TemplateModuleSource,
          fieldId,
          fieldLabel,
        })
      }
    }

    return fields
  }

  return {
    resolveTemplate,
    resolveHtml,
    createFieldChipHtml,
    createTableChipHtml,
    extractDynamicFields,
    formatFieldValue,
    getNestedValue,
  }
}
